import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import {
    CompanyUserService,
    CompanyUserSummary,
    VerificationMethodType,
} from '@treasury/domain/company/user';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { CompanyUserChallengeDetailsViewModel } from './company-user-challenge-details.vm';
import './partials/lock-details/lock-details.component';
import './partials/oob-details/oob-details.component';
import './partials/secure-token-details/secure-token-details.component';
import './partials/suspension-details/suspension-details.component';

export type UserAuthSettingsForm = {
    oobVerificationMethodType?: VerificationMethodType;
};

const tagName = 'tm-bo-user-auth-settings';
@customElement(tagName)
export class UserAuthSettingsComponent extends TmBaseComponent {
    @InjectProperty()
    declare public readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare public readonly dialogService: TmDialogService;

    @InjectProperty()
    declare public readonly ffService: FeatureFlagService;

    @InjectProperty()
    declare public readonly toastService: TmToastService;

    @property({ type: Object })
    public user?: CompanyUserSummary;

    @property({ type: Object })
    public company?: CompanyViewModel;

    @state()
    private challengeDetails?: CompanyUserChallengeDetailsViewModel;

    @state()
    private isUisEnabled: boolean = false;

    public render() {
        if (!this.challengeDetails) {
            return nothing;
        }

        const { isAuthMethodDisabled } = this.challengeDetails;

        return html`
            ${!isAuthMethodDisabled ? this.renderDetails() : this.renderDisabledDetails()}

            <jh-button
                label=${isAuthMethodDisabled ? 'Close' : 'Cancel'}
                appearance=${isAuthMethodDisabled ? 'primary' : 'tertiary'}
                @click=${() => this.dispatchEvent(new CustomEvent('close'))}
            ></jh-button>
        `;
    }

    protected async firstUpdated() {
        if (!this.user || !this.user.id || !this.company) {
            throw new Error('User and Company are required to render the component');
        }

        const challengeDetails = await this.companyUserService.getCompanyUserChallengeDetails(
            this.company.id,
            this.user.id
        );

        this.challengeDetails = new CompanyUserChallengeDetailsViewModel(challengeDetails);

        this.isUisEnabled = await this.ffService.isEnabled(Feature.UisEnabled);
    }

    private renderDetails() {
        return html`
            ${this.renderAuthDetails()}
            <tm-bo-suspension-details
                .userId=${this.user?.id}
                .challengeDetails=${this.challengeDetails}
                .company=${this.company}
            ></tm-bo-suspension-details>
            <tm-bo-lock-details
                .userId=${this.user?.id}
                .challengeDetails=${this.challengeDetails}
                .company=${this.company}
            ></tm-bo-lock-details>
            ${this.renderActions()}
        `;
    }

    private renderActions() {
        return html`<jh-button
            label="${this.isUisEnabled ? 'Reset TM Authentication' : 'Reset Authentication'}"
            appearance="primary"
            @click=${this.handleResetAuthentication}
        ></jh-button>`;
    }

    private renderDisabledDetails() {
        return html`<div class="mb-8">
            <p>Authentication is inactive for this company.</p>
        </div>`;
    }

    private renderAuthSummary() {
        return html`<div>
            <div class="mb-5">
                <label>User Authentication Status:</label>
                <span>${this.challengeDetails?.userStatusText}</span>
            </div>
            <div class="mb-5">
                <label>Authentication Method:</label>
                <span>${this.challengeDetails?.authMethodText}</span>
            </div>
        </div>`;
    }

    private renderAuthDetails() {
        if (!this.challengeDetails) {
            return nothing;
        }

        const {
            isUserRegistered,
            isAuthMethodOob,
            isAuthMethodSecureToken,
            authMethodText,
            verificationMethodType,
        } = this.challengeDetails;

        return html`
            ${this.renderAuthSummary()}
            ${
                isUserRegistered
                    ? html`
                          ${isAuthMethodOob
                              ? html`<tm-bo-oob-details
                                    .challengeDetails=${this.challengeDetails}
                                    .verificationMethodType=${verificationMethodType}
                                ></tm-bo-oob-details>`
                              : nothing}
                          ${isAuthMethodSecureToken
                              ? html`<tm-bo-secure-token-details
                                    .challengeDetails=${this.challengeDetails}
                                ></tm-bo-secure-token-details>`
                              : nothing}
                      `
                    : html`<p class="italic text-xs mb-5">
                          The current user has not yet registered for
                          <b>${authMethodText}</b> authentication. <br />
                          To see more info please have the user log out and then login back in
                          <br />
                          where they will be prompted to register.
                      </p>`
            }
        </div>`;
    }

    private async handleResetAuthentication(e: CustomEvent) {
        if (!this.challengeDetails || !this.company || !this.user?.id) {
            throw new Error('There was a problem unlocking the user.');
        }

        const selectElement = e.target as HTMLSelectElement;
        selectElement.disabled = true;

        const { reason } = await this.dialogService.open(
            html`<div>
                <p>
                    Are you sure you want to reset this user's credentials? Selecting reset will
                    prompt the user to register again at the next login.
                </p>
            </div>`,
            'Reset User',
            {
                buttons: [
                    {
                        label: 'Reset',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            selectElement.disabled = false;
            return;
        }
        const errorMessage = "There was an issue resetting the user's authentication.";
        try {
            const challengeDetails = await this.companyUserService.resetUserAuthentication(
                this.company.id,
                this.user.id
            );

            this.challengeDetails = new CompanyUserChallengeDetailsViewModel(challengeDetails);

            this.toastService.show(
                "User's authentication successfully reset",
                TmToastPosture.Success
            );
        } catch {
            this.toastService.show(errorMessage, TmToastPosture.Danger);
        } finally {
            selectElement.disabled = false;
        }
    }
}
